import React, {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from "react";

export interface SignatureCanvasHandle {
  getSignature: () => string | null;
  clear: () => void;
}

interface SignatureCanvasProps {
  className?: string;
}

const SignatureCanvas = forwardRef<SignatureCanvasHandle, SignatureCanvasProps>(
  ({ className = "" }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const isDrawingRef = useRef(false);
    const [hasDrawn, setHasDrawn] = useState(false);

    useImperativeHandle(
      ref,
      () => ({
        getSignature: () => {
          const canvas = canvasRef.current;
          if (!canvas) return null;
          if (!hasDrawn) return null;
          return canvas.toDataURL("image/png");
        },
        clear: handleClear,
      }),
      [hasDrawn]
    );

    const setupContext = useCallback((ctx: CanvasRenderingContext2D) => {
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = "#01545D";
    }, []);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const parent = canvas.parentElement;
      if (!parent) return;

      const resize = () => {
        const rect = parent.getBoundingClientRect();
        const ratio = window.devicePixelRatio || 1;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        // Save current drawing as an image before resizing
        const prevWidth = canvas.width;
        const prevHeight = canvas.height;
        let savedImage: ImageData | null = null;
        if (prevWidth > 0 && prevHeight > 0) {
          savedImage = ctx.getImageData(0, 0, prevWidth, prevHeight);
        }

        canvas.width = rect.width * ratio;
        canvas.height = rect.height * ratio;
        ctx.scale(ratio, ratio);
        setupContext(ctx);

        // Restore drawing if it existed
        if (savedImage) {
          ctx.putImageData(savedImage, 0, 0);
        }
      };

      resize();
      window.addEventListener("resize", resize);
      return () => window.removeEventListener("resize", resize);
    }, [setupContext]);

    const getContextAndPoint = (
      evt: React.PointerEvent<HTMLCanvasElement>
    ) => {
      const canvas = canvasRef.current;
      if (!canvas) return { ctx: null, x: 0, y: 0 };
      const rect = canvas.getBoundingClientRect();
      const ctx = canvas.getContext("2d");
      const x = evt.clientX - rect.left;
      const y = evt.clientY - rect.top;
      return { ctx, x, y };
    };

    const handlePointerDown = (
      evt: React.PointerEvent<HTMLCanvasElement>
    ) => {
      evt.preventDefault();
      const { ctx, x, y } = getContextAndPoint(evt);
      if (!ctx) return;
      setupContext(ctx);
      ctx.beginPath();
      ctx.moveTo(x, y);
      isDrawingRef.current = true;
      if (!hasDrawn) setHasDrawn(true);
      (evt.target as HTMLCanvasElement).setPointerCapture(evt.pointerId);
    };

    const handlePointerMove = (
      evt: React.PointerEvent<HTMLCanvasElement>
    ) => {
      if (!isDrawingRef.current) return;
      evt.preventDefault();
      const { ctx, x, y } = getContextAndPoint(evt);
      if (!ctx) return;
      ctx.lineTo(x, y);
      ctx.stroke();
    };

    const stopDrawing = (evt: React.PointerEvent<HTMLCanvasElement>) => {
      if (!isDrawingRef.current) return;
      isDrawingRef.current = false;
      try {
        (evt.target as HTMLCanvasElement).releasePointerCapture(
          evt.pointerId
        );
      } catch {
        // ignore
      }
    };

    const handleClear = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setHasDrawn(false);
    };

    return (
      <div className={"flex flex-col gap-2 " + className}>
        <div
          className="relative w-full border-2 border-dashed border-[#DCE1E8] rounded-xl bg-[#FAFBFC] overflow-hidden"
          style={{ height: "160px" }}
        >
          <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full cursor-crosshair"
            style={{ touchAction: "none" }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={stopDrawing}
            onPointerLeave={stopDrawing}
            onPointerCancel={stopDrawing}
          />
          {!hasDrawn && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <span className="text-[#B0B8C9] text-base select-none">
                Sign here
              </span>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleClear}
          className="self-start text-sm text-[#01545D] font-semibold underline px-2 py-1"
        >
          Clear
        </button>
      </div>
    );
  }
);

SignatureCanvas.displayName = "SignatureCanvas";

export default SignatureCanvas;
